import { game } from '../../Game';
import { Settings } from '../../Settings';
import * as emitterManager from '../../utils/EmitterManager';
import * as PhysicsParticleEmitter from '../../utils/PhysicsParticleEmitter';

// What falls out of a cut: organs (adults only, like bashes) and meat.
const GUTS = {
	head: ['Gore_Brain0000'],
	body: ['Gore_LungLeft0000', 'Gore_Stomach0000', 'Gore_Liver0000'],
	belly: ['Gore_Intestine0000', 'Gore_Intestine0000'],
};
const GUTS_MEAT = 3;
const GUTS_SPEED = 6;
export const EDGE_STAINS = 9;

const vec = new Box2D.b2Vec2(0, 0);
const drips = [];

// Wound decals (world points): a mix of blood and torn-open skin.
export const stainEdge = (body, points) => {
	points.forEach((point, i) => game.editor.queueDecalToBody(body, point, 'Decal.png', i % 3 === 0, 0.45 + Math.random() * 0.35, Math.random() * Math.PI * 2));
};

export const spillGuts = (humanoid, back, part, ring) => {
	const guts = humanoid.noGoreParticles ? [] : GUTS[part].slice();
	for (let i = 0; i < GUTS_MEAT; i++) guts.push(`Gore_Meat${1 + Math.floor(Math.random() * 6)}0000`);
	vec.Set(ring.x, ring.y);
	const centre = back.GetWorldPoint(vec);
	const velocity = back.GetLinearVelocity();
	const textures = game.editor.textures;
	PhysicsParticleEmitter.emit(guts, {x: centre.x, y: centre.y}, guts.length, 5, GUTS_SPEED, false, undefined, back.GetAngle(), undefined,
		{x: velocity.x, y: velocity.y}, textures.getChildIndex(back.myTexture) + 1, false);
};

export const burstRing = (type, body, ring, colors) => {
	vec.Set(ring.x, ring.y);
	const centre = body.GetWorldPoint(vec);
	const emitter = emitterManager.playOnceEmitter(type, body, {x: centre.x, y: centre.y}, 0, colors);
	if (!emitter) return;
	emitter.spawnCircle = {x: 0, y: 0, r: ring.r * Settings.PTM};
	const velocity = body.GetLinearVelocity();
	emitter.setSpawnVelocity(velocity.x * Settings.PTM, velocity.y * Settings.PTM);
};

// The bottom of a half's cut, whichever way it is turned.
const dripPoint = (body, ring) => {
	vec.Set(ring.x, ring.y);
	const centre = body.GetWorldPoint(vec);
	const gravity = game.world.GetGravity();
	const length = Math.sqrt(gravity.x * gravity.x + gravity.y * gravity.y) || 1;
	return {x: centre.x + gravity.x / length * ring.r * 0.8, y: centre.y + gravity.y / length * ring.r * 0.8};
};

export const startDrip = (type, body, ring, colors) => {
	const gravity = game.world.GetGravity();
	const emitter = emitterManager.playOnceEmitter(type, body, dripPoint(body, ring), Math.atan2(gravity.y, gravity.x) * 180 / Math.PI, colors);
	if (emitter) drips.push({emitter, body, ring, frame: body.myTexture});
};

// Drips follow their half until they run dry; let go as soon as the emitter
// stops, before the pool can hand it to something else.
export const updateDrips = () => {
	if (!drips.length) return;
	const gravity = game.world.GetGravity();
	const down = Math.atan2(gravity.y, gravity.x) * 180 / Math.PI;
	for (let i = drips.length - 1; i >= 0; i--) {
		const {emitter, body, ring, frame} = drips[i];
		if (!emitter.emit || emitter.body !== body || body.myTexture !== frame) {
			drips.splice(i, 1);
			continue;
		}
		if (body.destroyed) {
			emitter.emit = false;
			drips.splice(i, 1);
			continue;
		}
		const point = dripPoint(body, ring);
		emitter.spawnPos.set(point.x * Settings.PTM, point.y * Settings.PTM);
		emitter.rotation = down;
		const velocity = body.GetLinearVelocity();
		emitter.setSpawnVelocity(velocity.x * Settings.PTM, velocity.y * Settings.PTM);
	}
};

// While the beam is in a part: blood out of both places it breaks the skin,
// with stains and a gory mist.
export const bleedWhileCutting = (body, beam, intervals, step) => {
	const [t0, t1] = intervals[0];
	for (const [t, way] of [[t0, -1], [t1, 1]]) {
		const point = {x: beam.x + beam.dx * t, y: beam.y + beam.dy * t};
		if (step % 4 === 0) emitterManager.playOnceEmitter('blood', body, point, Math.atan2(beam.dy * way, beam.dx * way) * 180 / Math.PI);
		if (step % 8 === 0) game.editor.queueDecalToBody(body, point, 'Decal.png', false, 0.4, Math.random() * Math.PI * 2);
	}
	if (step % 16 === 0) {
		const t = (t0 + t1) / 2;
		emitterManager.playOnceEmitter('gorecloud', body, {x: beam.x + beam.dx * t, y: beam.y + beam.dy * t});
	}
};

// A dotted gash along the deepest line the beam reached, for parts that pulled back out.
export const scarPartialCut = (body, state) => {
	if (!state.frontier || !body.isFlesh) return;
	const {x0, y0, x1, y1} = state.frontier;
	const stamps = 6;
	for (let i = 0; i < stamps; i++) {
		const t = (i + 0.5) / stamps;
		vec.Set(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
		game.editor.queueDecalToBody(body, body.GetWorldPoint(vec), 'Decal.png', true, 0.3);
	}
};
